/**
 * AiSearchOverview — Google AI Overview-style search intelligence card.
 *
 * Collapsed: sparkle header + 2-3 line summary + filter chips + "Show more"
 * Expanded: full summary + TopPicks row + DealHeatmap + IntentBar + suggestions
 *
 * Sizes to content (no fixed min-height). Clean surface, not dark glass void.
 * The card explains what the AI did; the grid below shows annotated results.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { ChevronDown, Sparkles } from 'lucide-react';
import ConfidenceBadge from '../../../components/ConfidenceBadge/ConfidenceBadge';
import AiToolTimeline from '../../../components/AiToolTimeline';
import css from './AiSearchOverview.module.css';

// ── Icons ────────────────────────────────────────────────────────────

const SparkleIcon = ({ className }) => (
  <Sparkles className={className} size={20} strokeWidth={1.5} absoluteStrokeWidth />
);

const ChevronIcon = ({ up }) => (
  <ChevronDown
    size={14}
    strokeWidth={1.5}
    absoluteStrokeWidth
    style={{ transform: up ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}
  />
);

// ── Utilities ────────────────────────────────────────────────────────

function parseSuggestions(text) {
  const lines = text.split('\n');
  const suggestions = [];
  const bodyLines = [];
  for (const line of lines) {
    if (line.trimStart().startsWith('>> ')) {
      suggestions.push(line.trimStart().slice(3).trim());
    } else {
      bodyLines.push(line);
    }
  }
  return { body: bodyLines.join('\n').trim(), suggestions };
}

function parseBoldSegments(text) {
  const segments = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), bold: false });
    }
    segments.push({ text: match[1], bold: true });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), bold: false });
  }
  return segments;
}

function formatFilterChip(key, value) {
  if (!value) return null;
  if (key === 'pub_categoryLevel1') {
    return value.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  if (key === 'pub_categoryLevel2') {
    const parts = value.replace(/-/g, ' ').split(' ');
    return parts.length > 1
      ? parts.slice(1).join(' ').replace(/\b\w/g, c => c.toUpperCase())
      : value.replace(/\b\w/g, c => c.toUpperCase());
  }
  if (key === 'pub_categoryLevel3') {
    return value.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  if (key === 'price') {
    const [min, max] = String(value).split(',').map(Number);
    if (isNaN(min) || isNaN(max)) return value;
    if (min === 0) return `Under \u00a3${max}`;
    if (max >= 999999) return `Over \u00a3${min}`;
    return `\u00a3${min} \u2013 \u00a3${max}`;
  }
  if (key === 'pub_condition_id') {
    return value
      .replace('device-', '')
      .replace(/-/g, ' ')
      .replace('condition', '')
      .trim()
      .replace(/\b\w/g, c => c.toUpperCase());
  }
  if (key === 'sort') {
    if (value === 'price') return 'Price: low to high';
    if (value === '-price') return 'Price: high to low';
    if (value === '-createdAt') return 'Newest first';
    return value;
  }
  return null;
}

function getBadgeCssClass(badge) {
  if (['Best Value', 'Good Deal', 'Budget Pick'].includes(badge)) return css.pickBadgeGreen;
  if (badge === 'Caution') return css.pickBadgeAmber;
  return '';
}

// ── Sub-components ───────────────────────────────────────────────────

const SummaryProse = ({ text, isStreaming, isComplete, onHighlightClick }) => {
  const highlightsReady = useRef(false);
  const [showHighlights, setShowHighlights] = useState(false);

  useEffect(() => {
    if (isComplete && !highlightsReady.current) {
      highlightsReady.current = true;
      const timer = setTimeout(() => setShowHighlights(true), 200);
      return () => clearTimeout(timer);
    }
  }, [isComplete]);

  const segments = useMemo(() => parseBoldSegments(text), [text]);

  return (
    <div className={css.summaryText}>
      {segments.map((seg, i) =>
        seg.bold ? (
          <span
            key={i}
            className={classNames(css.highlight, {
              [css.highlightVisible]: showHighlights,
            })}
            onClick={() => onHighlightClick?.(seg.text)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter') onHighlightClick?.(seg.text);
            }}
          >
            {seg.text}
          </span>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        )
      )}
      {isStreaming && <span className={css.streamCursor} />}
    </div>
  );
};

const DealHeatmap = ({ listings, annotations }) => {
  const prices = useMemo(() => {
    return (listings || [])
      .map(l => l.attributes?.price?.amount || 0)
      .filter(p => p > 0)
      .map(p => p / 100);
  }, [listings]);

  if (prices.length < 2) return null;

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const avg = prices.reduce((a, b) => a + b, 0) / prices.length;

  const annotatedPrices = (annotations || [])
    .map(a => {
      const listing = (listings || []).find(l => l.id?.uuid === a.listing_id);
      return listing?.attributes?.price?.amount || 0;
    })
    .filter(p => p > 0)
    .map(p => p / 100);

  const bestDeal = annotatedPrices.length > 0 ? Math.min(...annotatedPrices) : min;
  const savings = Math.round(avg - bestDeal);

  return (
    <div className={css.heatmapSection}>
      <div className={css.heatmapBar}>
        <div
          className={classNames(css.heatmapZone, css.heatmapZoneGood)}
          style={{ left: 0, width: `${Math.min(((avg - range * 0.1 - min) / range) * 100, 50)}%` }}
        />
        <div
          className={classNames(css.heatmapZone, css.heatmapZoneAvg)}
          style={{
            left: `${Math.max(((avg - range * 0.1 - min) / range) * 100, 20)}%`,
            width: '20%',
          }}
        />
        <div
          className={classNames(css.heatmapZone, css.heatmapZoneFair)}
          style={{
            left: `${Math.min(((avg + range * 0.1 - min) / range) * 100, 70)}%`,
            right: 0,
            width: 'auto',
          }}
        />
        {prices.map((price, i) => {
          const pct = ((price - min) / range) * 100;
          const isGood = price < avg - range * 0.05;
          const isFair = price > avg + range * 0.1;
          return (
            <div
              key={i}
              className={classNames(css.heatmapDot, {
                [css.heatmapDotGood]: isGood,
                [css.heatmapDotFair]: isFair,
              })}
              style={{ left: `${Math.min(Math.max(pct, 2), 98)}%`, '--i': i }}
              title={`\u00a3${Math.round(price)}`}
            />
          );
        })}
      </div>
      <div className={css.heatmapLabels}>
        <span>{'\u00a3'}{Math.round(min)}</span>
        <span>{'\u00a3'}{Math.round(max)}</span>
      </div>
      <div className={css.heatmapStats}>
        <span className={css.heatmapStat}>
          <span className={css.heatmapStatValue}>{prices.length}</span> listings
        </span>
        <span className={css.heatmapStat}>
          Avg <span className={css.heatmapStatValue}>{'\u00a3'}{Math.round(avg)}</span>
        </span>
        {savings > 0 && (
          <span className={css.heatmapStat}>
            Best deal: <span className={css.heatmapStatValue}>
              {'\u00a3'}{Math.round(bestDeal)}
            </span>{' '}
            ({'\u2212\u00a3'}{savings})
          </span>
        )}
      </div>
    </div>
  );
};

const IntentBar = ({ intent }) => {
  if (!intent) return null;
  const chips = [];
  if (intent.product) chips.push({ label: intent.product, flex: false });
  if (intent.budget) chips.push({ label: intent.budget, flex: false });
  if (intent.condition) chips.push({ label: intent.condition, flex: false });
  if (intent.priorities) {
    intent.priorities.forEach(p => chips.push({ label: p, flex: false }));
  }
  if (intent.flexible) {
    intent.flexible.forEach(f => chips.push({ label: `Any ${f}`, flex: true }));
  }
  if (chips.length === 0) return null;
  return (
    <div className={css.intentBar}>
      {chips.map((chip, i) => (
        <span
          key={i}
          className={classNames(css.intentChip, {
            [css.intentChipFlexible]: chip.flex,
          })}
          style={{ '--i': i }}
        >
          {chip.label}
        </span>
      ))}
    </div>
  );
};

const WhyNotStrip = ({ excludedSummary, totalListings }) => {
  const [detailOpen, setDetailOpen] = useState(false);
  if (!excludedSummary || !excludedSummary.total_excluded) return null;

  const shownCount = totalListings || '?';
  const totalCount = (totalListings || 0) + excludedSummary.total_excluded;

  return (
    <div className={css.whyNotStrip}>
      <div className={css.whyNotHeader} onClick={() => setDetailOpen(prev => !prev)}>
        <span>
          Narrowed {totalCount} &rarr; {shownCount}
          {excludedSummary.reasons?.length > 0 && !detailOpen && (
            <>
              :{' '}
              {excludedSummary.reasons
                .slice(0, 2)
                .map(r => `${r.count} ${r.reason.toLowerCase()}`)
                .join(' \u00b7 ')}
            </>
          )}
        </span>
        <button className={css.whyNotToggle} type="button">
          <ChevronIcon up={detailOpen} />
        </button>
      </div>
      <div
        className={classNames(css.whyNotReasons, {
          [css.whyNotReasonsOpen]: detailOpen,
        })}
      >
        <div className={css.whyNotReasonsInner}>
          {excludedSummary.reasons?.length > 0 && (
            <ul className={css.whyNotList}>
              {excludedSummary.reasons.map((r, i) => (
                <li key={i} className={css.whyNotItem}>
                  <span className={css.whyNotDot} />
                  {r.count} {r.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const TopPicks = ({ annotations, listings, onPickClick, onQuickAction }) => {
  if (!annotations || annotations.length === 0) return null;

  const picks = annotations.map(ann => {
    const listing = (listings || []).find(l => l.id?.uuid === ann.listing_id);
    const firstImage = listing?.images?.[0];
    const imageUrl =
      firstImage?.attributes?.variants?.['listing-card-2x']?.url ||
      firstImage?.attributes?.variants?.['listing-card']?.url ||
      null;
    const price = listing?.attributes?.price?.amount;
    const title = listing?.attributes?.title || 'Listing';
    return { ...ann, imageUrl, price, title, listingId: ann.listing_id };
  });

  return (
    <div>
      <div className={css.topPicksLabel}>Top Picks</div>
      <div className={css.topPicksRow}>
        {picks.map((pick, i) => (
          <div
            key={pick.listingId}
            className={css.pickCard}
            style={{ '--i': i }}
            onClick={() => onPickClick?.(pick.listingId)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter') onPickClick?.(pick.listingId);
            }}
          >
            <div className={css.pickImageRow}>
              {pick.imageUrl ? (
                <img className={css.pickThumb} src={pick.imageUrl} alt={pick.title} loading="lazy" />
              ) : (
                <div className={css.pickThumb} />
              )}
              <div className={css.pickMeta}>
                <p className={css.pickTitle}>{pick.title}</p>
                <div className={css.pickPriceRow}>
                  {pick.price != null && (
                    <span className={css.pickPrice}>
                      {'\u00a3'}{(pick.price / 100).toFixed(0)}
                    </span>
                  )}
                  <span className={classNames(css.pickBadge, getBadgeCssClass(pick.badge))}>
                    {pick.badge}
                  </span>
                </div>
                {pick.score >= 70 && (
                  <div className={css.pickScore}>{pick.score}% match</div>
                )}
              </div>
            </div>
            <div className={css.pickReason}>{pick.reason}</div>
            {pick.condition_confidence && (
              <div className={css.pickCondition}>
                <div className={css.pickConditionItem}>
                  <span
                    className={
                      pick.condition_confidence.photo_count >= 4
                        ? css.conditionDotGood
                        : css.conditionDotWarn
                    }
                  />
                  {pick.condition_confidence.photo_count || 0} photos
                </div>
                <div className={css.pickConditionItem}>
                  <span
                    className={
                      pick.condition_confidence.has_battery_health
                        ? css.conditionDotGood
                        : css.conditionDotWarn
                    }
                  />
                  {pick.condition_confidence.has_battery_health ? 'Battery stated' : 'No battery info'}
                </div>
                <div className={css.pickConditionItem}>
                  <span
                    className={
                      pick.condition_confidence.seller_verified
                        ? css.conditionDotGood
                        : css.conditionDotWarn
                    }
                  />
                  {pick.condition_confidence.seller_verified ? 'Verified seller' : 'Unverified'}
                </div>
              </div>
            )}
            <div className={css.pickActions} onClick={e => e.stopPropagation()}>
              <button
                className={css.pickActionBtn}
                onClick={() => onQuickAction?.('message_seller', pick.listingId)}
                type="button"
              >
                Message
              </button>
              <button
                className={css.pickActionBtn}
                onClick={() => onQuickAction?.('watch_price', pick.listingId)}
                type="button"
              >
                Watch Price
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────

const AiSearchOverview = ({
  isActive = false,
  isComplete = false,
  error = null,
  intent = null,
  filters = null,
  excludedSummary = null,
  content = '',
  annotations = [],
  confidence = null,
  explanation = null,
  mode = null,
  toolSteps = [],
  hasRespondStarted = false,
  listings = [],
  query = '',
  onDismiss,
  onPickClick,
  onSuggestionClick,
  onQuickAction,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isActive) setIsExpanded(false);
  }, [isActive, query]);

  const { body: bodyText, suggestions } = useMemo(
    () => (content ? parseSuggestions(content) : { body: '', suggestions: [] }),
    [content]
  );

  const filterChips = useMemo(() => {
    if (!filters?.filters) return [];
    return Object.entries(filters.filters)
      .filter(([k]) => k !== 'keywords')
      .map(([key, value]) => {
        const label = formatFilterChip(key, value);
        return label ? { key, label } : null;
      })
      .filter(Boolean);
  }, [filters]);

  const handleHighlightClick = useCallback(
    highlightText => {
      if (!onPickClick || !listings?.length) return;
      const lower = highlightText.toLowerCase();
      const match = listings.find(l =>
        (l.attributes?.title || '').toLowerCase().includes(lower)
      );
      if (match) onPickClick(match.id.uuid);
    },
    [onPickClick, listings]
  );

  const hasExpandableContent =
    isComplete &&
    (annotations.length > 0 ||
      Boolean(intent) ||
      Boolean(excludedSummary?.total_excluded) ||
      suggestions.length > 0 ||
      confidence != null);

  const confidenceLabel =
    confidence >= 0.9
      ? 'High confidence'
      : confidence >= 0.6
      ? 'Moderate confidence'
      : confidence != null
      ? 'Low confidence'
      : null;

  const showVisibleFilters = (hasRespondStarted || isComplete) && filterChips.length > 0;
  const hasTrayItems = toolSteps.length > 0 || showVisibleFilters;

  return (
    <div className={css.outerWrap}>
      <div
        data-testid="ai-search-overview"
        className={classNames(css.root, {
          [css.rootActive]: isActive,
          [css.rootComplete]: isComplete,
        })}
      >
        {/* Header */}
        <div className={css.header}>
          <div className={css.headerLeft}>
            <SparkleIcon
              className={classNames(css.sparkleIcon, { [css.sparkleActive]: isActive })}
            />
            <span className={css.headerLabel}>PCIFIC AI</span>
            {isActive && <span className={css.liveIndicator} />}
          </div>
          {isComplete && onDismiss && (
            <button className={css.dismissBtn} onClick={onDismiss} type="button">
              Dismiss
            </button>
          )}
        </div>

        <div
          data-testid="ai-overview-chip-tray"
          className={classNames(css.filterTray, {
            [css.filterTrayHasItems]: hasTrayItems,
          })}
        >
          <AiToolTimeline
            steps={toolSteps}
            className={css.toolTimeline}
            dataTestId="ai-overview-tool-timeline"
          />

          {showVisibleFilters && (
            <div className={css.filterChips}>
              {filterChips.map(chip => (
                <span key={chip.key} className={css.filterChip}>
                  {chip.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Shimmer / Tool progress — persists through ALL tool phases until respond fires */}
        {isActive && !hasRespondStarted && (
          <div className={css.shimmerGroup}>
            <div className={css.shimmerLine} style={{ width: '90%' }} />
            <div className={css.shimmerLine} style={{ width: '75%' }} />
            <div className={css.shimmerLine} style={{ width: '60%' }} />
          </div>
        )}

        {/* Summary prose — only shows after respond tool fires */}
        {(hasRespondStarted || isComplete) && bodyText ? (
          <SummaryProse
            text={bodyText}
            isStreaming={isActive && !!bodyText}
            isComplete={isComplete}
            onHighlightClick={handleHighlightClick}
          />
        ) : null}

        {/* Error */}
        {error && <p className={css.errorText}>Unable to process query. Try a regular search.</p>}

        {/* Expanded content */}
        {isExpanded && (
          <div className={css.analysisBody}>
            <div className={css.divider} />
            {isComplete && annotations.length > 0 && (
              <DealHeatmap listings={listings} annotations={annotations} />
            )}
            <IntentBar intent={intent} />
            <WhyNotStrip
              excludedSummary={excludedSummary}
              totalListings={listings?.length || 0}
            />
            <TopPicks
              annotations={annotations}
              listings={listings}
              onPickClick={onPickClick}
              onQuickAction={onQuickAction}
            />
            {suggestions.length > 0 && (
              <div className={css.suggestions}>
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    className={css.suggestionPill}
                    onClick={() => onSuggestionClick?.(s)}
                    type="button"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {isComplete && confidence != null && (
              <div className={css.confidenceRow}>
                <ConfidenceBadge score={confidence} />
                {confidenceLabel && (
                  <span className={css.confidenceLabel}>{confidenceLabel}</span>
                )}
                {explanation && (
                  <p className={css.evidenceText}>{explanation}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Show more / Show less */}
        {hasExpandableContent && (
          <button
            className={css.expandButton}
            onClick={() => setIsExpanded(prev => !prev)}
            type="button"
          >
            {isExpanded ? 'Show less' : 'Show more'}
            <ChevronIcon up={isExpanded} />
          </button>
        )}
      </div>
    </div>
  );
};

export default AiSearchOverview;
